import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate';
import { applyArchive, buildArchive, DexieStorage, readArchive, type ModuleDataRow, type ModuleRow } from '@vp/core';
import { browserStorage, logger, storage } from '../platform';
import { download } from '../export';
import { readProfileLocal, writeProfileLocal } from './local';
import { createProfile, removeProfileRecord, type ProfileRecord } from './registry';
import { profileState } from './state';

/**
 * A whole profile in one file (docs/TZ-PROFILES.md 4.7), `.vpprofile` — a zip:
 *   manifest.json     {format: 'vp-profile', version, app, createdAt, profile: card + auto-lock}
 *   settings.json     the profile's settings (app settings, "seen" marks; not previews, not the folder)
 *   modules.json      installed modules (state, rights, settings); modules/<id>.vpm — their packages
 *   module-data.json  modules' own data across canvases (scope `app`)
 *   local.json        the profile's keys of localStorage (chats, consent, view choices…)
 *   canvases.vpz      every canvas (the usual canvas archive: content, files, modules' canvas data)
 * No PIN, no journal. Importing always makes a new profile (new id, no PIN, data in the browser).
 */
export const PROFILE_FORMAT = 'vp-profile';
export const PROFILE_FORMAT_VERSION = 1;
const MAX_FILE = 2 * 1024 * 1024 * 1024;

/** Settings that are not part of a profile's content: previews (redrawn) and where a folder was. */
const skipSetting = (key: string) => key.startsWith('thumb:') || key === 'storage.location' || key === '__probe';

function jsonable(v: unknown): boolean {
  try {
    return JSON.stringify(v) !== undefined && !(v instanceof Blob);
  } catch {
    return false;
  }
}

export async function buildProfileFile(): Promise<Uint8Array> {
  const p = profileState.current;
  if (!p) throw new Error('no profile is open');
  const db = browserStorage.db;
  const files: Zippable = {};
  const settings = (await db.settings.toArray()).filter((r) => !skipSetting(r.key) && jsonable(r.value));
  const modules: (Omit<ModuleRow, 'archive'> & { file: string })[] = [];
  for (const m of await db.modules.toArray()) {
    const file = `modules/${m.id}.vpm`;
    files[file] = new Uint8Array(await m.archive.arrayBuffer());
    const { archive: _a, ...rest } = m;
    void _a;
    modules.push({ ...rest, file });
  }
  const moduleData = (await db.moduleData.toArray()).filter((r) => r.scope === 'app');
  const ids = (await storage.listCanvases()).map((c) => c.id);
  files['canvases.vpz'] = await buildArchive(storage, ids, { app: __APP_VERSION__ });
  files['manifest.json'] = strToU8(
    JSON.stringify({
      format: PROFILE_FORMAT,
      version: PROFILE_FORMAT_VERSION,
      app: __APP_VERSION__,
      createdAt: new Date().toISOString(),
      profile: { firstName: p.firstName, lastName: p.lastName, avatar: p.avatar, logo: p.logo, lockAfter: p.lockAfter },
      canvases: ids.length,
    }),
  );
  files['settings.json'] = strToU8(JSON.stringify(settings));
  files['modules.json'] = strToU8(JSON.stringify(modules));
  files['module-data.json'] = strToU8(JSON.stringify(moduleData));
  files['local.json'] = strToU8(JSON.stringify(readProfileLocal(p.id)));
  return zipSync(files, { level: 6 });
}

/** "Export profile": the file is saved by the browser. */
export async function exportProfile(): Promise<void> {
  const p = profileState.current;
  if (!p) return;
  const data = await buildProfileFile();
  const safe = `${p.firstName}${p.lastName ? '-' + p.lastName : ''}`.replace(/[^\p{L}\p{N}_-]+/gu, '-').slice(0, 60) || 'profile';
  download(new Blob([data as BlobPart], { type: 'application/zip' }), `${safe}-${new Date().toISOString().slice(0, 10)}.vpprofile`);
  logger.info('profile.exported', { bytes: data.byteLength });
}

export class ProfileFileError extends Error {}

interface Manifest {
  format: string;
  version: number;
  profile: { firstName?: string; lastName?: string; avatar?: string; logo?: string; lockAfter?: number };
}

/** A profile file becomes a new profile (not opened); throws ProfileFileError for a file that is not one. */
export async function importProfileData(data: Uint8Array): Promise<ProfileRecord> {
  if (data.byteLength > MAX_FILE) throw new ProfileFileError('the file is too big');
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(data);
  } catch {
    throw new ProfileFileError('not a profile file');
  }
  const json = <T>(name: string, fallback: T): T => {
    const f = entries[name];
    if (!f) return fallback;
    try {
      return JSON.parse(strFromU8(f)) as T;
    } catch {
      throw new ProfileFileError(`${name} is damaged`);
    }
  };
  const m = json<Manifest | null>('manifest.json', null);
  if (!m || m.format !== PROFILE_FORMAT) throw new ProfileFileError('not a profile file');
  if (m.version > PROFILE_FORMAT_VERSION) throw new ProfileFileError('the file is from a newer version of the app');
  const canvases = entries['canvases.vpz'] ? await readArchive(entries['canvases.vpz']) : null;
  const pic = (s: unknown) => (typeof s === 'string' && s.startsWith('data:image/') ? s : '');
  const p = await createProfile({ firstName: String(m.profile.firstName ?? '').trim() || 'Profile', lastName: String(m.profile.lastName ?? ''), avatar: pic(m.profile.avatar), logo: pic(m.profile.logo) });
  const db = new DexieStorage(p.db);
  try {
    for (const r of json<{ key: string; value: unknown }[]>('settings.json', [])) if (typeof r?.key === 'string' && !skipSetting(r.key)) await db.setSetting(r.key, r.value);
    for (const r of json<(Omit<ModuleRow, 'archive'> & { file: string })[]>('modules.json', [])) {
      const bytes = entries[r.file];
      if (!bytes || typeof r.id !== 'string') continue;
      const { file: _f, ...row } = r;
      void _f;
      await db.modules.put({ ...row, archive: new Blob([bytes as BlobPart], { type: 'application/zip' }) });
    }
    for (const r of json<ModuleDataRow[]>('module-data.json', [])) if (r?.scope === 'app') await db.moduleData.put(r);
    // canvases get new ids: chats and other keys that name a canvas follow them
    const created = canvases ? await applyArchive(db, canvases) : [];
    const renamed = new Map<string, string>();
    canvases?.canvases.forEach((c, i) => created[i] && renamed.set(c.meta.id, created[i]));
    const swap = (s: string) => {
      let out = s;
      for (const [from, to] of renamed) out = out.split(from).join(to);
      return out;
    };
    const local: Record<string, string> = {};
    for (const [k, v] of Object.entries(json<Record<string, string>>('local.json', {}))) if (typeof v === 'string') local[swap(k)] = swap(v);
    writeProfileLocal(p.id, local);
  } catch (e) {
    db.close();
    // nothing half-made stays behind
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(p.db);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    });
    await removeProfileRecord(p.id);
    throw e;
  }
  db.close();
  return p;
}

/** "Import profile": pick a file; null when the person closed the dialog. */
export function importProfileFile(): Promise<ProfileRecord | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.vpprofile,application/zip';
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return resolve(null);
      f.arrayBuffer().then((b) => importProfileData(new Uint8Array(b)).then(resolve, reject), reject);
    };
    input.addEventListener('cancel', () => resolve(null));
    input.click();
  });
}
